import { useState, useCallback, useMemo, useEffect } from 'react';
import {
	DragStartEvent,
	DragOverEvent,
	DragEndEvent,
	PointerSensor,
	useSensor,
	useSensors,
	UniqueIdentifier,
} from '@dnd-kit/core';
import {
	DragAndDropId,
	RankWithSecurities,
	SecurityWithRank,
	UpsertSecurityRankParams,
	VIRTUAL_RESIDUE_RANK_ID,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/securityRankingTypes';
import { handleSecurityDragEnd } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/handleSecurityDragEndEvent';
import { makeSecurityRankId } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/securityRankingUtils.ts';
import { SecurityFeatureType, SecurityRankDefinitionRes } from '@dealstack/shared/lib/generated';

// Optimistic tier reassignment: drop the moving group from its source tier and append it (deduped)
// to the target tier, leaving every other tier untouched.
function reassignRankSecurities(
	rank: RankWithSecurities,
	sourceRankId: string | undefined,
	targetRankId: string,
	group: SecurityWithRank[],
	groupIds: Set<string>
): RankWithSecurities {
	if (rank.id === sourceRankId) {
		return {
			...rank,
			securities: rank.securities.filter((sec) => !groupIds.has(makeSecurityRankId(sec))),
		};
	}

	if (rank.id === targetRankId) {
		const present = new Set(rank.securities.map((sec) => makeSecurityRankId(sec)));
		return {
			...rank,
			securities: [
				...rank.securities,
				...group.filter((sec) => !present.has(makeSecurityRankId(sec))),
			],
		};
	}

	return rank;
}

interface Props {
	serverPersonRanks: SecurityRankDefinitionRes[];
	serverPersonSecurities: SecurityWithRank[];
	serverPersonRanksUpdatedAt: number;
	serverPersonSecuritiesUpdatedAt: number;
	selectedEntityId: string;
	securityRankError: unknown;
	upsertSecurityRank: (params: UpsertSecurityRankParams) => void;
	deleteSecurityRank: (security: SecurityWithRank) => void;
}

export const useSecurityDragAndDrop = ({
	serverPersonRanks,
	serverPersonSecurities,
	serverPersonRanksUpdatedAt,
	serverPersonSecuritiesUpdatedAt,
	selectedEntityId,
	securityRankError,
	upsertSecurityRank,
	deleteSecurityRank,
}: Props) => {
	const [dragActiveId, setDragActiveId] = useState<UniqueIdentifier | null>(null);
	const [dragOverId, setDragOverId] = useState<UniqueIdentifier | null>(null);
	const [dragSourceId, setDragSourceId] = useState<UniqueIdentifier | null>(null);

	// Local state for optimistic updates
	const [localRanks, setLocalRanks] = useState<RankWithSecurities[]>([]);
	const [unrankedSecurities, setUnrankedSecurities] = useState<SecurityWithRank[]>([]);

	// Get the security being actively dragged for the DragOverlay
	const activeSecurity = useMemo(() => {
		if (!dragActiveId) {
			return null;
		}

		return (
			serverPersonSecurities.find((sec) => makeSecurityRankId(sec) === dragActiveId) || null
		);
	}, [dragActiveId, serverPersonSecurities]);

	const sortedRanks = useMemo(() => {
		const sorted = [...localRanks].sort((a, b) => b.sortOrder - a.sortOrder);

		if (!activeSecurity) {
			return sorted;
		}

		const placeholderRank: RankWithSecurities = {
			id: DragAndDropId.placeholderRank,
			displayName: '',
			dealId: '',
			sortOrder: -1,
			displaySortOrder: 0,
			securities: [],
			issuerId: selectedEntityId,
			isTracking: false,
			isResidue: false,
			deleted: false,
			updatedAt: '',
			createdAt: '',
			validUnrankedSecurities: [],
			isPlaceholder: true,
		};

		if (dragSourceId && dragSourceId !== DragAndDropId.sidebar) {
			const sourceRankIndex = sorted.findIndex((rank) => rank.id === dragSourceId);

			if (sourceRankIndex !== -1) {
				sorted.splice(sourceRankIndex + 1, 0, placeholderRank);
				return sorted;
			}
		}

		sorted.unshift(placeholderRank);

		return sorted;
	}, [localRanks, activeSecurity, selectedEntityId, dragSourceId]);

	// Sync server data with local state - only when data actually updates
	useEffect(() => {
		if (serverPersonRanks && serverPersonSecurities) {
			const isResidueCard = (sec: SecurityWithRank) =>
				sec.securityFeatureType === SecurityFeatureType.RESIDUE;

			// Right to residue is never ranked (no SecurityRank row exists; the DB forbids one).
			// Every residue card - warrant rows included - renders in the single residue tier,
			// synthesised locally when the issuer has no persisted residue rank.
			let residueRank = serverPersonRanks.find((rank) => rank.isResidue);
			const ranks = [...serverPersonRanks];

			if (!residueRank && serverPersonSecurities.some(isResidueCard)) {
				residueRank = {
					id: VIRTUAL_RESIDUE_RANK_ID,
					displayName: 'Residue',
					dealId: '',
					issuerId: selectedEntityId,
					sortOrder: Math.min(0, ...ranks.map((rank) => rank.sortOrder)) - 1,
					isTracking: false,
					isResidue: true,
					deleted: false,
					updatedAt: '',
					createdAt: '',
				};
				ranks.push(residueRank);
			}

			const ranksMap = new Map<string, SecurityWithRank[]>();

			ranks.forEach((rank) => {
				ranksMap.set(rank.id, []);
			});

			serverPersonSecurities.forEach((sec) => {
				const targetRankId = isResidueCard(sec)
					? residueRank?.id
					: sec.securityRank?.rankId;

				if (targetRankId) {
					ranksMap.get(targetRankId)?.push(sec);
				}
			});

			const unrankedSecurities = serverPersonSecurities.filter(
				(sec) => !isResidueCard(sec) && !sec.securityRank
			);
			const newRanksWithSecurities = [...ranks]
				.sort((a, b) => b.sortOrder - a.sortOrder)
				.map((rank, idx) => ({
					...rank,
					displaySortOrder: idx + 1,
					securities: ranksMap.get(rank.id) || [],
					validUnrankedSecurities: unrankedSecurities.filter((sec) => {
						const featureType = sec.securityFeatureType;

						if (sec.warrantOverId) {
							return false;
						}

						if (rank.isTracking) {
							return featureType === SecurityFeatureType.TRACKING;
						} else if (rank.isResidue) {
							// Residue cards are never in the sidebar, so nothing can be
							// added to the residue tier.
							return false;
						} else {
							return (
								featureType !== SecurityFeatureType.TRACKING &&
								featureType !== SecurityFeatureType.RESIDUE
							);
						}
					}),
				}));

			setLocalRanks(newRanksWithSecurities);
			setUnrankedSecurities(unrankedSecurities);
		}
		// Only re-run when timestamps change or there's an error (intentional - prevents infinite loops)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [serverPersonRanksUpdatedAt, serverPersonSecuritiesUpdatedAt, securityRankError]);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		})
	);

	const handleDragStart = useCallback((event: DragStartEvent) => {
		setDragActiveId(event.active.id);
		setDragSourceId(event.active.data.current?.draggedFromId ?? null);
	}, []);

	const handleDragOver = useCallback((event: DragOverEvent) => {
		const overId = event.over?.id ?? null;
		setDragOverId((prev) => (prev === overId ? prev : overId));
	}, []);

	const handleDragCancel = useCallback(() => {
		setDragActiveId(null);
		setDragOverId(null);
		setDragSourceId(null);
	}, []);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			setDragSourceId(null);
			handleSecurityDragEnd({
				event,
				setDragActiveId,
				setDragOverId,
				localRanks,
				setLocalRanks,
				setUnrankedSecurities,
				serverPersonSecurities,
				selectedEntityId,
				upsertSecurityRank,
				deleteSecurityRank,
			});
		},
		[
			localRanks,
			serverPersonSecurities,
			selectedEntityId,
			upsertSecurityRank,
			deleteSecurityRank,
		]
	);

	// Click-assigns must move the card optimistically like the drag path; otherwise its visibility
	// hinges on refetch ordering. The mutation's refetch reconciles, and error reverts via the
	// sync effect above. Moves the warrant shadow cards with it, as the server does.
	const assignSecurityToRankLocally = useCallback(
		(security: SecurityWithRank, targetRankId: string) => {
			const sourceRankId = security.securityRank?.rankId;
			if (sourceRankId === targetRankId) {
				return;
			}

			const group = [
				security,
				...serverPersonSecurities.filter(
					(sec) =>
						sec.warrantOverId === security.securityId &&
						sec.securityFeatureType === security.securityFeatureType
				),
			];
			const groupIds = new Set(group.map((sec) => makeSecurityRankId(sec)));

			setUnrankedSecurities((prev) =>
				prev.filter((sec) => !groupIds.has(makeSecurityRankId(sec)))
			);
			setLocalRanks((prev) =>
				prev.map((rank) =>
					reassignRankSecurities(rank, sourceRankId, targetRankId, group, groupIds)
				)
			);
		},
		[serverPersonSecurities]
	);

	// Get all securities in the drag group (main card + warrant cards)
	const draggedSecurityGroup = useMemo(() => {
		if (!activeSecurity) {
			return [];
		}

		const group = [activeSecurity];
		const warrantCards = serverPersonSecurities.filter(
			(sec) =>
				sec.warrantOverId === activeSecurity.securityId &&
				sec.securityFeatureType === activeSecurity.securityFeatureType
		);

		group.push(...warrantCards);

		return group;
	}, [activeSecurity, serverPersonSecurities]);

	return {
		sensors,
		dragActiveId,
		dragOverId,
		dragSourceId,
		sortedRanks,
		localRanks,
		unrankedSecurities,
		handleDragStart,
		handleDragOver,
		handleDragEnd,
		handleDragCancel,
		assignSecurityToRankLocally,
		activeSecurity,
		draggedSecurityGroup,
	};
};
